import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Sparkles, Wind, Heart, Leaf, Check } from 'lucide-react';
import { services, businessInfo } from '../data/mock';

const DetoxSupportPage = () => {
  const detoxAreas = [
    {
      title: "Parasite Support",
      description: "Our gentle protocols support the body's natural ability to address parasitic concerns. We use targeted nutritional strategies and herbal remedies to create an environment that supports internal balance.",
      icon: Shield,
      color: "#6E9EB9"
    },
    {
      title: "Heavy Metal Support",
      description: "We offer targeted support for addressing heavy metal accumulation. Our protocols focus on supporting the body's natural detoxification pathways while providing essential nutrients for cellular health.",
      icon: Sparkles,
      color: "#AF9EB8"
    },
    {
      title: "Environmental Chemical Support",
      description: "Modern life exposes us to countless environmental toxins. Our protocols support the body's natural ability to process and eliminate these chemicals while strengthening overall resilience.",
      icon: Wind,
      color: "#93A8C7"
    },
    {
      title: "Microbial Balance Support",
      description: "We provide support for addressing yeast overgrowth, bacterial imbalances, and viral concerns. Our approach focuses on restoring healthy microbial balance throughout the body.",
      icon: Heart,
      color: "#A08CA8"
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#AF9EB8]/10 via-white to-[#F7F8FA] overflow-hidden">
        <div className="absolute top-20 left-10 w-64 h-64 bg-[#AF9EB8]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-[#6E9EB9]/10 rounded-full blur-3xl" />
        
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#AF9EB8]/10 rounded-full mb-6">
              <Sparkles size={16} className="text-[#AF9EB8]" />
              <span className="text-sm font-medium text-[#AF9EB8]">Comprehensive Support</span>
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium text-[#2C2F36] mb-6">
              Detox & Restoration
            </h1>
            <p className="text-xl text-[#4A5568] mb-8 max-w-3xl mx-auto">
              We emphasize comprehensive detoxification support to help restore balance to your body. 
              Our gentle protocols work with your body's natural healing abilities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-pink">
                Schedule Consultation
                <ArrowRight size={18} />
              </Link>
              <Link to="/nutritional-therapy" className="btn-secondary">
                Learn About Nutrition
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Detox Support */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1 bg-[#6E9EB9]/10 rounded-full text-sm font-medium text-[#6E9EB9] mb-4">
                Our Approach
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-6">
                Why Detoxification Support Matters
              </h2>
              <p className="text-[#4A5568] mb-6 leading-relaxed">
                In today's world, our bodies are constantly exposed to toxins from our environment, 
                food, water, and personal care products. Over time, these toxins can accumulate and 
                burden our natural detoxification systems.
              </p>
              <p className="text-[#4A5568] mb-6 leading-relaxed">
                At Serenity Natural Health & Wellness, we provide comprehensive detoxification support 
                that works with your body's innate healing abilities. Our gentle, natural protocols are 
                designed to support the elimination of unwanted substances while nourishing and 
                strengthening your body.
              </p>
              <p className="text-[#4A5568] leading-relaxed">
                Our approach combines nutritional therapy with targeted detox protocols to address 
                multiple areas of concern, from parasites and heavy metals to environmental chemicals 
                and microbial imbalances.
              </p>
            </div>
            <div className="bg-gradient-to-br from-[#AF9EB8]/10 to-[#6E9EB9]/10 rounded-2xl p-8">
              <h3 className="font-serif text-2xl font-medium text-[#2C2F36] mb-6">
                Signs You May Benefit from Detox Support:
              </h3>
              <ul className="space-y-4">
                {[
                  "Persistent fatigue or low energy",
                  "Digestive issues or bloating",
                  "Brain fog or difficulty concentrating",
                  "Skin problems or breakouts",
                  "Joint pain or stiffness",
                  "Frequent headaches",
                  "Unexplained weight changes",
                  "Weakened immune function"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check size={20} className="text-[#AF9EB8] mt-0.5 flex-shrink-0" />
                    <span className="text-[#4A5568]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Detox Areas */}
      <section className="section-padding bg-[#F7F8FA]">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-[#AF9EB8]/10 rounded-full text-sm font-medium text-[#AF9EB8] mb-4">
              Areas of Support
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-4">
              Our Detox Support Services
            </h2>
            <div className="floral-divider w-24 mx-auto mb-6" />
            <p className="text-[#4A5568] max-w-2xl mx-auto">
              We address multiple areas of detoxification to provide comprehensive support 
              for your body's natural cleansing processes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {detoxAreas.map((area, index) => {
              const IconComponent = area.icon;
              return (
                <div 
                  key={index}
                  className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow"
                >
                  <div 
                    className="w-14 h-14 rounded-full flex items-center justify-center mb-6"
                    style={{ backgroundColor: `${area.color}15` }}
                  >
                    <IconComponent size={28} style={{ color: area.color }} />
                  </div>
                  <h3 className="font-serif text-xl font-medium text-[#2C2F36] mb-4">
                    {area.title}
                  </h3>
                  <p className="text-[#4A5568] leading-relaxed">
                    {area.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-[#6E9EB9]/10 rounded-full text-sm font-medium text-[#6E9EB9] mb-4">
              The Process
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-4">
              How Our Detox Support Works
            </h2>
            <div className="floral-divider w-24 mx-auto" />
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: 1, title: "Assessment", desc: "Comprehensive evaluation of your health history and current concerns" },
              { step: 2, title: "Protocol Design", desc: "Custom detox protocol based on your specific needs" },
              { step: 3, title: "Gentle Support", desc: "Natural methods that work with your body's healing abilities" },
              { step: 4, title: "Monitor & Adjust", desc: "Ongoing support and protocol refinement as needed" }
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-12 h-12 bg-[#6E9EB9] text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                  {item.step}
                </div>
                <h3 className="font-serif text-lg font-medium text-[#2C2F36] mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-[#4A5568]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Note */}
      <section className="section-padding bg-[#F7F8FA]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl p-8 border-l-4 border-[#6E9EB9]">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-[#6E9EB9]/10 rounded-full flex-shrink-0">
                  <Leaf size={24} className="text-[#6E9EB9]" />
                </div>
                <div>
                  <h3 className="font-serif text-xl font-medium text-[#2C2F36] mb-4">
                    Our Supportive Approach
                  </h3>
                  <p className="text-[#4A5568] mb-4 leading-relaxed">
                    As Traditional Naturopaths, we focus on supporting your body's natural healing 
                    abilities. Our protocols are designed to gently support detoxification while 
                    nourishing your body with essential nutrients.
                  </p>
                  <p className="text-[#4A5568] leading-relaxed">
                    We recommend combining detox support with our nutritional therapy services for 
                    the most comprehensive approach to wellness. Many clients find that addressing 
                    nutrition alongside detoxification provides the best foundation for lasting health.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#AF9EB8] to-[#A08CA8] text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-medium mb-6">
            Ready to Support Your Body's Natural Detox?
          </h2>
          <p className="text-white/90 max-w-2xl mx-auto mb-8">
            Let us help you develop a personalized detox support protocol. Schedule your 
            consultation with {businessInfo.owner.name} today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="px-8 py-4 bg-white text-[#AF9EB8] rounded-full font-semibold hover:bg-white/90 transition-colors inline-flex items-center justify-center gap-2"
            >
              Book Consultation
              <ArrowRight size={18} />
            </Link>
            <Link 
              to="/services" 
              className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-full font-semibold hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DetoxSupportPage;